import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import {
  colors,
  mixins,
  animations,
  spaces,
} from '../../core/styles/theme/default';
import { breakpoints } from '../../core/styles/theme/layout';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, SwiperOptions } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';

function setVisibleSlides(count = 1) {
  let styles: string[] | string = [];

  for (let i = 0; i < count; i++) {
    styles.push(`.swiper-slide-active${' + .swiper-slide'.repeat(i)}`);
  }

  styles =
    styles.join(', ') +
    ` {
    opacity: 1;

    &::after {
     content: none;
    }
  }`;

  return styles;
}

const StyledSlider = styled.div(
  ({
    showHiddenSlides,
    disableOpacity,
    swiperParams,
    theme,
  }: {
    showHiddenSlides?: boolean;
    disableOpacity?: boolean;
    swiperParams: SwiperOptions;
    theme?: Theme;
  }) => css`
    .swiper {
      .swiper-pagination {
        width: 100%;
        position: static;
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        justify-content: center;
        order: 1;
        margin: ${rem(spaces.delta)} 0 0;

        .swiper-pagination-bullet {
          margin: ${rem(10)};
          opacity: 1;
          border: ${rem(1)} solid ${colors.nightBlue700};
          width: ${rem(10)};
          height: ${rem(10)};
          background: transparent;

          &-active {
            background: ${colors.nightBlue700};
          }
        }

        &-lock {
          display: none;
        }
      }

      .swiper-slide {
        height: auto;
      }

      &.swiper-initialized {
        ${showHiddenSlides &&
        css`
          overflow: visible;

          ${!disableOpacity &&
          css`
            .swiper-slide {
              opacity: 0.2;
              transition: opacity ${animations.default};

              &::after {
                content: '';
                display: block;
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: 1;
              }
            }

            ${setVisibleSlides()}

            ${mixins.MQ('medium')} {
              ${setVisibleSlides(
                swiperParams.breakpoints?.[breakpoints.medium]
                  .slidesPerView as number,
              )}
            }

            ${mixins.MQ('large')} {
              ${setVisibleSlides(
                swiperParams.breakpoints?.[breakpoints.large]
                  .slidesPerView as number,
              )}
            }

            ${mixins.MQ('ultra')} {
              ${setVisibleSlides(
                swiperParams.breakpoints?.[breakpoints.ultra]
                  .slidesPerView as number,
              )}
            }
          `}
        `};
      }

      &:not(.swiper-initialized) {
        .swiper-wrapper {
          ${mixins.responsiveStyles({
            gap: {
              medium:
                swiperParams.breakpoints?.[breakpoints.medium].spaceBetween,
              large: swiperParams.breakpoints?.[breakpoints.large].spaceBetween,
              ultra: swiperParams.breakpoints?.[breakpoints.ultra].spaceBetween,
            },
          })}
        }

        .swiper-slide {
          ${mixins.responsiveStyles({
            'flex-basis': {
              zero: '100%',
              medium: `calc((100% / ${
                swiperParams.breakpoints?.[breakpoints.medium].slidesPerView
              }) - ((${
                (swiperParams.breakpoints?.[breakpoints.medium]
                  .slidesPerView as number) - 1
              } * ${rem(
                swiperParams.breakpoints?.[breakpoints.medium]
                  .spaceBetween as number,
              )}) / ${
                swiperParams.breakpoints?.[breakpoints.medium].slidesPerView
              }))`,
              large: `calc((100% / ${
                swiperParams.breakpoints?.[breakpoints.large].slidesPerView
              }) - ((${
                (swiperParams.breakpoints?.[breakpoints.large]
                  .slidesPerView as number) - 1
              } * ${rem(
                swiperParams.breakpoints?.[breakpoints.large]
                  .spaceBetween as number,
              )}) / ${
                swiperParams.breakpoints?.[breakpoints.large].slidesPerView
              }))`,
              ultra: `calc((100% / ${
                swiperParams.breakpoints?.[breakpoints.ultra].slidesPerView
              }) - ((${
                (swiperParams.breakpoints?.[breakpoints.ultra]
                  .slidesPerView as number) - 1
              } * ${rem(
                swiperParams.breakpoints?.[breakpoints.ultra]
                  .spaceBetween as number,
              )}) / ${
                swiperParams.breakpoints?.[breakpoints.ultra].slidesPerView
              }))`,
            },
          })}
        }
      }
    }

    ${theme?.stylesExtension?.slider}
  `,
);

const Slider = ({
  children,
  showHiddenSlides = false,
  disableOpacity = false,
  slidesPerView = 2,
  onPaginationVisibilityChange,
}: SliderProps) => {
  const swiperParams: SwiperOptions = {
    modules: [Pagination],
    pagination: { clickable: true },
    centerInsufficientSlides: true,
    slidesPerView: 1,
    spaceBetween: spaces.beta,
    breakpoints: {
      [breakpoints.medium]: {
        slidesPerView: Math.min(2, slidesPerView),
        spaceBetween: spaces.delta,
      },
      [breakpoints.large]: {
        slidesPerView: slidesPerView,
        spaceBetween: spaces.delta,
      },
      [breakpoints.ultra]: {
        slidesPerView: slidesPerView,
        spaceBetween: spaces.epsilon,
      },
    },
    ...(onPaginationVisibilityChange && {
      onLock: () => onPaginationVisibilityChange(false),
      onUnlock: () => onPaginationVisibilityChange(true),
    }),
  };

  return (
    <StyledSlider
      disableOpacity={disableOpacity}
      showHiddenSlides={showHiddenSlides}
      swiperParams={swiperParams}
    >
      <Swiper {...swiperParams}>
        {children.filter(Boolean).map((item) => (
          <SwiperSlide key={`slide-${item.key}`}>{item}</SwiperSlide>
        ))}
      </Swiper>
    </StyledSlider>
  );
};

type SliderProps = {
  children: Array<any>;
  showHiddenSlides?: boolean;
  disableOpacity?: boolean;
  slidesPerView?: number;
  onPaginationVisibilityChange?: (isVisible: boolean) => void;
};

export default Slider;
